import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { Branches, BranchFilters } from '../models';
import * as branchesService from '../services/branches.service';

export const useBranches = (defaultFilters: Partial<BranchFilters & { searchParams: { ids: string[] } }> = {}) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<BranchFilters>>(otherDefaultFilters);

  const cacheData = branchesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<BranchFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to identity filter changes
  useEffect(() => {
    updateFilters(otherDefaultFilters);
  }, [otherDefaultFilters.customerKey, updateFilters]);

  const { data, isLoading, isError } = useQuery(branchesService.branchesQueryOptions(filters));

  // Check if requested IDs are already in main data to prevent duplicate requests
  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    branchesService.branchesQueryOptions({ search: { id: missingIds.join(',') } }, { enabled: missingIds.length > 0 }),
  );

  const mergedMap: Branches['map'] = {
    ...(data?.map ?? {}),
    ...(cacheData?.map ?? {}),
    ...(preloadData?.map ?? {}),
  };

  return {
    data: {
      list: Object.values(mergedMap).sort((a, b) => a.branchDesc.localeCompare(b.branchDesc)),
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
